import { useCallback, useEffect, useState } from 'react';
import { ErroApi } from '../lib/api';
import { buscarPlacar, emReais, type LinhaDoPlacar } from '../lib/equipes';
import { Alerta, Botao, Cartao } from '../componentes/Interface';
import Layout from '../componentes/Layout';

/** As três primeiras posições ganham cor, como na planilha da igreja. */
const PODIO = [
  { faixa: 'bg-tinta-900 text-white', numero: 'bg-white/20 text-white', medalha: '🥇' },
  { faixa: 'bg-amber-400 text-tinta-900', numero: 'bg-tinta-900/10 text-tinta-900', medalha: '🥈' },
  { faixa: 'bg-rose-700 text-white', numero: 'bg-white/20 text-white', medalha: '🥉' },
];

function mesEmPalavras(mes: string): string {
  if (!mes) return '';
  const texto = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    month: 'long',
    year: 'numeric',
  }).format(new Date(`${mes}-01T12:00:00-03:00`));
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

function semanaEmPalavras(segunda: string): string {
  if (!segunda) return '';
  const inicio = new Date(`${segunda}T12:00:00-03:00`);
  const fim = new Date(inicio);
  fim.setDate(fim.getDate() + 6);
  const dia = (d: Date) =>
    new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: 'short' })
      .format(d)
      .replace('.', '');
  return `${dia(inicio)} a ${dia(fim)}`;
}

function outroMes(mes: string, passos: number): string {
  const d = new Date(`${mes}-01T12:00:00-03:00`);
  d.setMonth(d.getMonth() + passos);
  return d.toISOString().slice(0, 7);
}

/** 6.6 vira "6,6" e 12 vira "12", como a igreja escreve. */
const emNumero = (v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(1).replace('.', ','));

export default function Placar() {
  const [mes, setMes] = useState('');
  const [semana, setSemana] = useState('');
  const [equipes, setEquipes] = useState<LinhaDoPlacar[]>([]);
  const [subs, setSubs] = useState<LinhaDoPlacar[]>([]);
  const [quem, setQuem] = useState<'equipes' | 'subs' | 'planilha'>('equipes');
  const [visao, setVisao] = useState<'mes' | 'semana'>('mes');
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async (qual?: string) => {
    try {
      const r = await buscarPlacar(qual);
      setEquipes(r.equipes);
      setSubs(r.subs);
      setMes(r.mes);
      setSemana(r.semana);
      setErro(null);
    } catch (e) {
      // ARMADILHA 12: o motivo aparece, nao some
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar o placar.');
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const lista = quem === 'equipes' ? equipes : subs;
  const colunas = [
    { chave: 'sem', nome: 'Sem' },
    { chave: 'arena', nome: 'Arena' },
    { chave: 'dom', nome: 'Dom' },
    { chave: 'novos', nome: 'Novos' },
    { chave: 'visita', nome: 'Visita' },
    { chave: 'iv', nome: 'IV' },
    { chave: 'celulas', nome: 'Células' },
    { chave: 'revisaoSem', nome: 'Rev. sem' },
    { chave: 'revisaoTotal', nome: 'Rev. total' },
    { chave: 'discipulado', nome: 'Discip.' },
  ] as const;
  const chaveOrdem = visao === 'mes' ? 'posicaoMes' : 'posicaoSemana';
  const chavePontos = visao === 'mes' ? 'pontosMes' : 'pontosSemana';
  const chavePessoas = visao === 'mes' ? 'pessoasMes' : 'pessoasSemana';
  const ordenadas = [...lista].sort(
    (a, b) => a[chaveOrdem] - b[chaveOrdem] || a.nome.localeCompare(b.nome),
  );
  const maior = Math.max(1, ...ordenadas.map((e) => e[chavePontos]));
  const campeao = ordenadas.find((e) => e[chavePontos] > 0);

  return (
    <Layout
      titulo="Placar"
      subtitulo="Quem levou mais gente aos cultos. Domingo vale 1, arena 0,5 e o culto da semana 0,3."
    >
      {erro && (
        <div className="mb-4 max-w-2xl">
          <Alerta>{erro}</Alerta>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex rounded-xl bg-white p-1 shadow-cartao">
          {(
            [
              { valor: 'equipes', rotulo: 'Equipes' },
              { valor: 'subs', rotulo: 'Melhor sub' },
              { valor: 'planilha', rotulo: 'Planilha' },
            ] as const
          ).map((v) => (
            <button
              key={v.valor}
              onClick={() => setQuem(v.valor)}
              className={`rounded-lg px-4 py-1.5 text-sm font-semibold transition ${
                quem === v.valor ? 'bg-tinta-900 text-white' : 'text-tinta-600 hover:bg-tinta-100'
              }`}
            >
              {v.rotulo}
            </button>
          ))}
        </div>

        <div className="ml-auto flex rounded-xl bg-white p-1 shadow-cartao">
          {(
            [
              { valor: 'mes', rotulo: 'Mês' },
              { valor: 'semana', rotulo: `Semana ${semanaEmPalavras(semana)}` },
            ] as const
          ).map((v) => (
            <button
              key={v.valor}
              onClick={() => setVisao(v.valor)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                visao === v.valor ? 'bg-tinta-900 text-white' : 'text-tinta-600 hover:bg-tinta-100'
              }`}
            >
              {v.rotulo}
            </button>
          ))}
        </div>
      </div>

      {/* faixa do campeao, do jeito que se anuncia no culto */}
      {campeao && (
        <div className="mb-4 overflow-hidden rounded-2xl bg-tinta-900 text-white shadow-cartao">
          <div className="flex flex-wrap items-center gap-4 p-5">
            <span className="text-4xl">🏆</span>
            <span className="min-w-0 flex-1">
              <span className="block text-xs uppercase tracking-[0.2em] text-white/70">
                {quem === 'equipes' ? 'Equipe na frente' : 'Melhor sub'} ·{' '}
                {visao === 'mes' ? mesEmPalavras(mes) : `semana de ${semanaEmPalavras(semana)}`}
              </span>
              <span className="block truncate text-2xl font-black">{campeao.nome}</span>
              {campeao.equipe && (
                <span className="block text-sm text-tinta-300">
                  {campeao.equipe}
                  {campeao.liderNome ? ` · ${campeao.liderNome}` : ''}
                </span>
              )}
            </span>
            <span className="text-right">
              <span className="block text-4xl font-black text-white">
                {emNumero(campeao[chavePontos])}
              </span>
              <span className="block text-[11px] uppercase tracking-wide text-tinta-400">pontos</span>
            </span>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 pb-3">
        <Botao variante="suave" onClick={() => carregar(outroMes(mes, -1))}>
          ‹ mês
        </Botao>
        <span className="text-sm font-semibold text-tinta-700">{mesEmPalavras(mes)}</span>
        <Botao variante="suave" onClick={() => carregar(outroMes(mes, 1))}>
          mês ›
        </Botao>
      </div>

      {quem === 'planilha' ? (
        <Cartao className="overflow-x-auto p-0">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="bg-amber-500 text-left text-xs font-bold uppercase tracking-wide text-tinta-900">
                <th className="px-3 py-2">Equipe</th>
                <th className="px-3 py-2">Sub</th>
                <th className="px-3 py-2">Líder</th>
                {colunas.map((c) => (
                  <th key={c.chave} className="px-2 py-2 text-center">
                    {c.nome}
                  </th>
                ))}
                <th className="px-2 py-2 text-center">Membresia</th>
                <th className="px-3 py-2 text-right">PD</th>
              </tr>
            </thead>
            <tbody>
              {[...subs]
                .sort(
                  (a, b) =>
                    (a.equipe ?? '').localeCompare(b.equipe ?? '') || a.nome.localeCompare(b.nome),
                )
                .map((sub, i) => {
                  const extras = (visao === 'mes' ? sub.extrasMes : sub.extrasSemana) ?? undefined;
                  const pontos = sub[chavePontos];
                  return (
                    <tr
                      key={sub.id}
                      className={`border-b border-tinta-100 ${i % 2 ? 'bg-tinta-50' : ''}`}
                    >
                      <td className="px-3 py-2 font-semibold uppercase text-tinta-700">
                        {sub.equipe}
                      </td>
                      <td className="px-3 py-2 font-medium text-tinta-900">{sub.nome}</td>
                      <td className="px-3 py-2 text-tinta-500">{sub.liderNome ?? '–'}</td>
                      {colunas.map((c) => {
                        const valor = extras ? Number(extras[c.chave] ?? 0) : 0;
                        return (
                          <td
                            key={c.chave}
                            className={`px-2 py-2 text-center ${
                              valor > 0 ? 'font-semibold text-tinta-900' : 'text-tinta-300'
                            }`}
                          >
                            {valor}
                          </td>
                        );
                      })}
                      <td
                        className={`px-2 py-2 text-center font-black ${
                          pontos > 0 ? 'bg-tinta-900 text-white' : 'text-tinta-300'
                        }`}
                      >
                        {emNumero(pontos)}
                      </td>
                      <td className="px-3 py-2 text-right text-tinta-500">
                        {emReais(extras ? Number(extras.pdSem ?? 0) : 0)}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </Cartao>
      ) : (
      <Cartao className="p-0">
        <ul>
          {ordenadas.map((e) => {
            const lugar = e[chaveOrdem];
            const pontos = e[chavePontos];
            const podio = pontos > 0 ? PODIO[lugar - 1] : undefined;
            return (
              <li
                key={`${quem}-${e.id}`}
                className={`flex items-center gap-3 border-b border-tinta-100 px-4 py-3 last:border-0 ${
                  podio ? podio.faixa : ''
                }`}
              >
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-sm font-black ${
                    podio ? podio.numero : 'bg-tinta-100 text-tinta-600'
                  }`}
                >
                  {pontos > 0 ? lugar : '–'}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-bold uppercase tracking-wide">
                    {podio ? `${podio.medalha} ` : ''}
                    {e.nome}
                  </span>
                  <span
                    className={`block truncate text-xs ${podio ? 'opacity-80' : 'text-tinta-400'}`}
                  >
                    {e.equipe ? `${e.equipe}${e.liderNome ? ` · ${e.liderNome}` : ''}` : null}
                    {!e.equipe && `${e[chavePessoas]} ${e[chavePessoas] === 1 ? 'pessoa' : 'pessoas'}`}
                  </span>
                  {!podio && (
                    <span className="mt-1 block h-1.5 w-full overflow-hidden rounded-full bg-tinta-100">
                      <span
                        className="block h-full rounded-full bg-tinta-400"
                        style={{ width: `${Math.round((pontos / maior) * 100)}%` }}
                      />
                    </span>
                  )}
                </span>
                <span className="shrink-0 text-right">
                  <span className="block text-xl font-black">{emNumero(pontos)}</span>
                  <span className={`block text-[10px] uppercase ${podio ? 'opacity-80' : 'text-tinta-400'}`}>
                    {e[chavePessoas]} {e[chavePessoas] === 1 ? 'pessoa' : 'pessoas'}
                  </span>
                </span>
              </li>
            );
          })}
          {ordenadas.length === 0 && (
            <li className="px-4 py-6 text-sm text-tinta-400">
              Nenhuma chamada feita ainda neste mês.
            </li>
          )}
        </ul>
      </Cartao>
      )}

      <p className="mt-3 text-xs text-tinta-400">
        A conta é a mesma da planilha: cada pessoa que veio no domingo vale 1 ponto, no sábado 0,5 e
        no culto da semana 0,3. Vir de manhã e à noite no domingo conta uma vez só.
      </p>
    </Layout>
  );
}
